import { Box } from "@mui/material";
import memoize from "memoize-one";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  areEqual,
  FixedSizeGrid as Grid,
  FixedSizeList as List,
} from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

import { useDrive } from "./DriveContext";
import DriveItemView from "./DriveItemView";
import { DriveItem } from "./types";

type DriveItemBodyHandle = {
  resetCache: () => void;
};

type DriveItemsBodyProps = {
  items?: DriveItem[];
  onClickItem: (event: React.MouseEvent, item: DriveItem) => void;
  onDoubleClickItem: (event: React.MouseEvent, item: DriveItem) => void;
  onContextMenuItem: (event: React.MouseEvent, item: DriveItem) => void;
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadNextPage: () => boolean;
  columnCount: number;
};

const GUTTER_SIZE = 8;

const DriveItemsItem = React.memo(
  ({ data, index, columnIndex, rowIndex, style }: any) => {
    const {
      items,
      viewType,
      selectedItems,
      selectableItemTypes,
      disabledItems,
      onClickItem,
      onDoubleClickItem,
      onContextMenuItem,
      columnCount,
      maxWidth,
    } = data;

    const gridIndex =
      parseFloat(rowIndex) * columnCount + parseFloat(columnIndex);
    const tableIndex = index;
    const item = viewType === "grid" ? items[gridIndex] : items[tableIndex];

    if (!item) return null;

    const isSelected = selectedItems?.some((i: DriveItem) => i.id === item.id);
    const isSelectable =
      !selectableItemTypes || selectableItemTypes.includes(item.__typename);
    const isDisabled = disabledItems?.some((i: DriveItem) => i.id === item.id);

    return (
      <DriveItemView
        key={item.id}
        viewType={viewType}
        item={item}
        isSelected={isSelected}
        isSelectable={isSelectable && !isDisabled}
        onClick={onClickItem}
        onDoubleClick={onDoubleClickItem}
        onContextMenu={onContextMenuItem}
        style={{
          ...style,
          left: parseFloat(style.left) + GUTTER_SIZE,
          top: parseFloat(style.top) + GUTTER_SIZE,
          maxWidth,
        }}
      />
    );
  },
  areEqual,
);

const DriveItemsView = forwardRef(
  (
    {
      items,
      onClickItem,
      onDoubleClickItem,
      onContextMenuItem,
      hasNextPage,
      isNextPageLoading,
      loadNextPage,
      columnCount,
    }: DriveItemsBodyProps,
    ref,
  ) => {
    const { viewType, selectedItems, selectableItemTypes, disabledItems } =
      useDrive();

    const itemsArray = items ?? [];
    // If there are more items to be loaded then add an extra row to hold a loading indicator.
    const itemCount = hasNextPage
      ? itemsArray.length + (viewType === "grid" ? columnCount : 1)
      : itemsArray.length;

    // Only load 1 page of items at a time.
    // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
    const loadMoreItems = isNextPageLoading
      ? () => {}
      : () => {
          loadNextPage();
        };

    // Every row is loaded except for our loading indicator row.
    const isItemLoaded = (index: number) =>
      !hasNextPage || index < itemsArray.length;

    const infiniteLoaderRef = useRef<InfiniteLoader>(null);

    useImperativeHandle(
      ref,
      () => {
        return {
          resetCache() {
            if (infiniteLoaderRef.current) {
              infiniteLoaderRef.current.resetloadMoreItemsCache();
            }
          },
        };
      },
      [],
    );

    const createItemData = memoize(
      (
        items,
        viewType,
        selectedItems,
        selectableItemTypes,
        disabledItems,
        onClickItem,
        onContextMenuItem,
        onDoubleClickItem,
        isItemLoaded,
        columnCount,
      ) => ({
        items,
        viewType,
        selectedItems,
        selectableItemTypes,
        disabledItems,
        onClickItem,
        onContextMenuItem,
        onDoubleClickItem,
        isItemLoaded,
        columnCount,
      }),
    );

    const itemData = createItemData(
      itemsArray,
      viewType,
      selectedItems,
      selectableItemTypes,
      disabledItems,
      onClickItem,
      onContextMenuItem,
      onDoubleClickItem,
      isItemLoaded,
      columnCount,
    );

    return viewType === "grid" ? (
      <AutoSizer>
        {({ height, width }) => (
          <InfiniteLoader
            ref={infiniteLoaderRef}
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            threshold={4 * columnCount}
          >
            {({ onItemsRendered, ref }) => (
              <Grid
                itemData={itemData}
                width={width}
                height={height}
                columnCount={columnCount}
                rowCount={Math.ceil(itemsArray.length / columnCount)}
                columnWidth={width / columnCount - 24 / columnCount}
                rowHeight={width / columnCount + 56 + GUTTER_SIZE}
                style={{ overflowY: "auto" }}
                onItemsRendered={(gridProps) => {
                  onItemsRendered({
                    overscanStartIndex:
                      gridProps.overscanRowStartIndex * columnCount,
                    overscanStopIndex:
                      gridProps.overscanRowStopIndex * columnCount,
                    visibleStartIndex:
                      gridProps.visibleRowStartIndex * columnCount,
                    visibleStopIndex:
                      gridProps.visibleRowStopIndex * columnCount,
                  });
                }}
                ref={ref}
              >
                {DriveItemsItem}
              </Grid>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    ) : (
      <AutoSizer>
        {({ height, width }) => (
          <InfiniteLoader
            ref={infiniteLoaderRef}
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            threshold={32}
          >
            {({ onItemsRendered, ref }) => (
              <List
                ref={ref}
                itemData={itemData}
                width={width}
                height={height}
                itemCount={itemsArray.length}
                itemSize={56}
                style={{
                  overflowY: "auto",
                }}
                onItemsRendered={onItemsRendered}
              >
                {DriveItemsItem}
              </List>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    );
  },
);

const DriveItemsBody = forwardRef<DriveItemBodyHandle, DriveItemsBodyProps>(
  (
    {
      items,
      onClickItem,
      onDoubleClickItem,
      onContextMenuItem,
      hasNextPage,
      isNextPageLoading,
      loadNextPage,
      columnCount,
    },
    ref,
  ) => {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <DriveItemsView
          ref={ref}
          items={items}
          onClickItem={onClickItem}
          onDoubleClickItem={onDoubleClickItem}
          onContextMenuItem={onContextMenuItem}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columnCount={columnCount}
        />
      </Box>
    );
  },
);

export default DriveItemsBody;
